/*
Fork addition: in-game transport suggestion for a product flow.

Modes and tier capacities come from the game's own logistic data
(gameData.getLogistic): each logistic entry is one transport mode
(Pipe, Flat conveyor, Molten channel, ...) with its tiers ordered by
capacity. Both the logistic capacities and the solver's flow values
use the game's per-minute convention, so they compare directly.

Per the user's call, loose bulk is reported as "N/A — truck" (no pipe
or belt is suggested for it), and products with no logistic entry at
all (electricity, workers, maintenance and other special/virtual
goods) are plain "N/A".
*/
import {GameItemType} from '#types/constants';
import type {GameItem} from '#types/game-data';
import type {GameData} from './data';
import {formatNumber} from './format';

export interface TransportSuggestion {
    //short label for chips, e.g. "Pipe II", "Molten channel", "N/A — truck"
    label: string;
    //full explanation for tooltips
    title: string;
    //fork addition: capacity warning state, shared by the link chip
    //and the inspector caption so the rule lives in this one place.
    //'tight' = flow runs at >= 90% of the shown tier's capacity;
    //'over' = flow exceeds the largest single transport, so parallel
    //runs are needed (the "×N" label case)
    warning?: 'tight' | 'over';
}

//a tier running at or above this fraction of its capacity is 'tight'
export const TRANSPORT_TIGHT_FRACTION = 0.9;

export function transportSuggestion(
    gameData: GameData,
    product: GameItem | undefined,
    flowPerMinute: number | undefined,
): TransportSuggestion | undefined {
    if(!product)
        return undefined;
    const flowText = (flowPerMinute !== undefined)
        ? `flow ${formatNumber(flowPerMinute)}/min`
        : 'flow not solved yet';
    if(product.type === GameItemType.Loose) {
        return {
            label: 'N/A — truck',
            title: `Loose bulk moves by truck — no pipe or belt (${flowText})`,
        };
    }
    const logistic = gameData.getLogistic(product)
        .find((entry) => entry.transport.length);
    if(!logistic) {
        return {
            label: 'N/A',
            title: `No in-game transport for ${product.label} — it flows directly (${flowText})`,
        };
    }
    const modeLabel = logistic.label || logistic.name;
    if(flowPerMinute === undefined) {
        return {
            label: modeLabel,
            title: `${modeLabel} (${flowText})`,
        };
    }
    //tiers are ordered slowest to fastest by the data parser
    const tiers = logistic.transport;
    const covering = tiers.find((tier) => tier.countPerSecond >= flowPerMinute);
    if(covering) {
        const tierLabel = covering.item?.label || covering.name;
        return {
            label: tierLabel,
            title: `${tierLabel} · capacity ${formatNumber(Math.round(covering.countPerSecond))}/min · ${flowText}`,
            warning: (flowPerMinute >= covering.countPerSecond * TRANSPORT_TIGHT_FRACTION)
                ? 'tight'
                : undefined,
        };
    }
    const biggest = tiers[tiers.length - 1];
    if(!biggest)
        return undefined;
    const tierLabel = biggest.item?.label || biggest.name;
    const parallel = Math.max(2, Math.ceil(flowPerMinute / biggest.countPerSecond));
    return {
        label: `${tierLabel} ×${parallel}`,
        title: `${parallel}× ${tierLabel} in parallel · capacity ${formatNumber(Math.round(biggest.countPerSecond))}/min each · ${flowText}`,
        warning: 'over',
    };
}
